import { Avatar, Tooltip } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import theme from 'src/setup/theme';

import { getBrandIcon } from '../../../modules/data';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    padding: '0 12px',
    paddingTop: '14px',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  brandInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  brandAvatar: {
    width: 18,
    height: 18,
    backgroundColor: grey[100],
    '& img': {
      objectFit: 'contain',
      padding: 2,
    },
  },
  brandAvatarLarge: {
    width: 26,
    height: 26,
    backgroundColor: grey[100],
    '& img': {
      objectFit: 'contain',
      padding: 3,
    },
  },
  brandCount: {
    fontSize: 12,
    fontWeight: 800,
    minWidth: 16,
  },
  modelsWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
    overflow: 'hidden',
  },
  modelsText: {
    fontSize: 12,
    color: grey[800],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  moreCount: {
    fontSize: 10,
    fontWeight: 500,
    color: theme.palette.primary.main,
    backgroundColor: grey[200],
    borderRadius: 4,
    padding: '1px 4px',
    flexShrink: 0,
  },
  moreBrands: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    fontSize: 10,
    fontWeight: 500,
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
    padding: '1px 4px',
    cursor: 'default',
  },
  totalCount: {
    position: 'fixed',
    top: 0,
    left: 10,
    fontSize: 10,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
    padding: '1px 4px',
    cursor: 'default',
    width: 'fit-content',
  },
});

const MAX_MODELS_DISPLAYED = 3;
const MAX_BRANDS_DISPLAYED = 3;

interface PackageBrandCellProps {
  brands: { [key: string]: number };
  models?: { [key: string]: number };
  totalCars?: number;
}

/**
 * Extract short model name from full model string.
 * E.g., "Volkswagen Golf Style 2.0 TDI SCR 110kW 7-Gang DSG" → "Golf Style"
 */
const extractShortModelName = (fullModel: string, brand: string): string => {
  // Remove brand prefix (case-insensitive)
  const withoutBrand = fullModel.replace(new RegExp(`^${brand}\\s+`, 'i'), '');
  // Take first 2-3 words as model name (up to engine specs)
  const words = withoutBrand.split(' ');
  const modelWords: string[] = [];
  for (const word of words) {
    // Stop at engine specs (numbers with units like 2.0, 110kW, etc.)
    if (/^\d+(\.\d+)?/.test(word) || /^\d+kW$/.test(word)) break;
    modelWords.push(word);
    if (modelWords.length >= 3) break;
  }
  return modelWords.join(' ') || withoutBrand.split(' ')[0];
};

/**
 * Group models by brand, matching by prefix (case-insensitive).
 */
const groupModelsByBrand = (
  brands: { [key: string]: number },
  models: { [key: string]: number },
): { [brand: string]: { shortName: string; count: number }[] } => {
  const result: { [brand: string]: { shortName: string; count: number }[] } = {};
  const brandNames = Object.keys(brands);

  // Initialize empty arrays for all brands
  brandNames.forEach((brand) => {
    result[brand] = [];
  });

  // Match each model to a brand
  Object.entries(models).forEach(([fullModelName, count]) => {
    const lowerModel = fullModelName.toLowerCase();
    const matchedBrand = brandNames.find((brand) => lowerModel.startsWith(brand.toLowerCase()));

    if (matchedBrand) {
      const shortName = extractShortModelName(fullModelName, matchedBrand);
      // Check if this short name already exists for the brand
      const existing = result[matchedBrand].find((m) => m.shortName === shortName);
      if (existing) {
        existing.count += count;
      } else {
        result[matchedBrand].push({ shortName, count });
      }
    }
  });

  return result;
};

export const PackageBrandCell = ({ brands, models, totalCars }: PackageBrandCellProps): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation();

  const brandEntries = useMemo(() => Object.entries(brands).sort((a, b) => b[1] - a[1]), [brands]);

  const modelsByBrand = useMemo(() => (models ? groupModelsByBrand(brands, models) : {}), [brands, models]);

  const displayedBrands = brandEntries.slice(0, MAX_BRANDS_DISPLAYED);
  const remainingBrands = brandEntries.slice(MAX_BRANDS_DISPLAYED);
  const remainingBrandsTooltip = remainingBrands.map(([brand, count]) => `${brand} (${count})`).join(', ');

  return (
    <div>
      <span className={classes.totalCount}>
        {totalCars} {t('common.car', { count: totalCars })}
      </span>
      <div className={classes.container}>
        {displayedBrands.map(([brand]) => {
          const brandModels = (modelsByBrand[brand] || []).sort((a, b) => b.count - a.count);
          const brandCount = brandModels.reduce((sum, m) => sum + m.count, 0);
          const allModelsText = brandModels.map((m) => `${m.count} x ${m.shortName}`).join(', ');

          // Show max 3 models, then "+X" badge for the rest
          const displayedModels = brandModels.slice(0, MAX_MODELS_DISPLAYED);
          const remainingCount = brandModels.length - MAX_MODELS_DISPLAYED;
          const displayText = displayedModels.map((m) => `${m.count} x ${m.shortName}`).join(', ');

          return (
            <div key={brand} className={classes.brandRow}>
              <div className={classes.brandInfo}>
                <span className={classes.brandCount}>{brandCount}</span>
                <Tooltip title={brand} arrow>
                  <Avatar
                    className={brandEntries.length === 1 ? classes.brandAvatarLarge : classes.brandAvatar}
                    src={getBrandIcon(brand)}
                    alt={brand}
                    imgProps={{
                      onError: (e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      },
                    }}
                  >
                    {brand.charAt(0)}
                  </Avatar>
                </Tooltip>
              </div>
              <Tooltip title={allModelsText || brand} arrow>
                <div className={classes.modelsWrapper}>
                  <span className={classes.modelsText}>{displayText || brand}</span>
                  {remainingCount > 0 && (
                    <span className={classes.moreCount}>
                      +{remainingCount} {t('car.model', { count: remainingCount })}
                    </span>
                  )}
                </div>
              </Tooltip>
            </div>
          );
        })}
        {remainingBrands.length > 0 && (
          <Tooltip title={remainingBrandsTooltip} arrow>
            <span className={classes.moreBrands}>
              +{remainingBrands.length} {t('car.brand', { count: remainingBrands.length })}
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
